import { RosterTab, TimeEntryType, Venue } from "../model";

type VenueShortInfo = Pick<Venue, "id" | "name">;

function hasCustomTab(venue: Readonly<VenueShortInfo>, customTabs: Readonly<RosterTab[]>): boolean {
    return customTabs.some(
        (customTab) =>
            customTab.venue === venue.id &&
            customTab.name === venue.name &&
            customTab.time === undefined &&
            customTab.types === undefined &&
            customTab.departments === undefined
    );
}

function expandSickTypes(types: TimeEntryType[]): TimeEntryType[] {
    const typesSet = new Set(types);
    if (typesSet.has(TimeEntryType.Sick)) {
        typesSet.add(TimeEntryType.SickInKUG);
        typesSet.add(TimeEntryType.ChildSick);
    } else {
        typesSet.delete(TimeEntryType.SickInKUG);
        typesSet.delete(TimeEntryType.ChildSick);
    }
    return [...typesSet];
}

/** Generates a list of roster tabs for based on the user's profile and the venues they have access to.
 *
 * @param customRosterTabs - The custom roster tabs defined by the user.
 * @param accessibleActiveVenues - Active venues that the user has access to.
 *
 * Returns all the custom tabs, and a default tab for every venue if not already defined in the custom tabs.
 */
export function getProfileOrDefaultRosterTabs(
    customRosterTabs: Readonly<RosterTab[]>,
    accessibleActiveVenues: Readonly<VenueShortInfo[]>
): RosterTab[] {
    const defaultRosterTabs: () => RosterTab[] = () =>
        Array.from(accessibleActiveVenues)
            .filter((venue) => !hasCustomTab(venue, customRosterTabs))
            .map((venue) => ({
                venue: venue.id,
                name: venue.name,
            }));

    const accessibleVenueIds = accessibleActiveVenues.map((venue) => venue.id);
    const rosterTabs = [...customRosterTabs, ...defaultRosterTabs()].filter((tab) =>
        accessibleVenueIds.includes(tab.venue)
    );
    rosterTabs.forEach((tab) => {
        if (tab.types) {
            tab.types = expandSickTypes(tab.types);
        }
    });

    return rosterTabs;
}
